import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, User, Mail, Lock, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", country: "US" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 6) {
      setError("Please enter a valid phone number.");
      return;
    }
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success("Account created! Welcome to Giftopay.");
      navigate(location.state?.from || "/sell");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-[80vh] bg-[#F8FAFC] flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-navy-gradient flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-2xl font-bold text-[#0B3C5D]">
              Gifto<span className="text-[#00C853]">pay</span>
            </span>
          </Link>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_8px_32px_rgba(11,60,93,0.06)] p-8">
          <h1 className="font-display text-2xl font-bold text-[#0B3C5D]">Create your account</h1>
          <p className="mt-1 text-sm text-slate-500">Start selling gift cards in under 2 minutes.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <Field icon={User} label="Full name" type="text" value={form.name} onChange={update("name")} placeholder="Jane Doe" testid="reg-name" />
            <Field icon={Mail} label="Email" type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" testid="reg-email" />
            <Field icon={Phone} label="Phone number" type="tel" value={form.phone} onChange={update("phone")} placeholder="+1 555 123 4567" testid="reg-phone" inputMode="tel" autoComplete="tel" />
            <Field icon={Lock} label="Password" type="password" value={form.password} onChange={update("password")} placeholder="At least 8 characters" testid="reg-password" />

            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Country</label>
              <select
                value={form.country}
                onChange={update("country")}
                className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
                data-testid="reg-country"
              >
                <option value="US">🇺🇸 United States</option>
                <option value="UK">🇬🇧 United Kingdom</option>
                <option value="AE">🇦🇪 United Arab Emirates</option>
                <option value="CA">🇨🇦 Canada</option>
              </select>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700" data-testid="reg-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#0B3C5D] text-white py-3.5 font-semibold btn-primary disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="reg-submit"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create Account
            </button>

            <p className="text-xs text-slate-500 text-center">
              By signing up you agree to our{" "}
              <Link to="/terms" className="underline text-[#0B3C5D]">Terms</Link> and{" "}
              <Link to="/privacy" className="underline text-[#0B3C5D]">Privacy Policy</Link>.
            </p>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            Already have an account?{" "}
            <Link
              to="/login"
              state={location.state}
              className="text-[#0B3C5D] font-semibold hover:text-[#00C853]"
              data-testid="reg-to-login"
            >
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, testid, ...rest }) {
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</label>
      <div className="mt-1.5 relative">
        <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          required
          data-testid={testid}
          {...rest}
          className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
        />
      </div>
    </div>
  );
}
